// Outback Station — stage layout (src/world/stages/outback/). The stage owns every file in this folder:
//   layout.js    level geometry (this file)        props.js    prop pack + placements (set dressing)
//   surfaces.js  stage surface materials (texlib)  murals.js   stage decals / signage (mural atlas)
import { PATTERN, B, R, O, OCT } from '../../mapkit.js';
import { SURF } from './surfaces.js';
import { MURAL_IDS } from './murals.js';

// ------------------------------------------------------------------------------------------------------------
// Outback Station — a sun-baked Australian cattle & sheep station nestled in an arid red-earth gorge inspired by the
// Flinders Ranges. Completely landlocked: a continuous red-ochre valley floor framed by layered sandstone bluffs and
// wind-swept red sand hills, with a sunken dry creek bed cutting across the centre around a towering Southern Cross
// bore windmill.
//   • spawn: Homestead Veranda (y = 2.6, z = ±41.8) — wide jarrah timber veranda deck under a bullnose corrugated iron
//     roof, with a central stone stair down to the Station Yard and timber ramps out to both flanks
//   • centre: Sunken Dry Creek Bed (y = -0.7) spanned by two timber-and-grate cattle-grid bridges (y = 0.08) flanking
//     the central octagonal Bore Windmill & Tank platform (y = 0.42)
//   • right lane (-X for Alpha, +X for Bravo): Shearing Shed & Stockyards — timber loading dock (y = 1.2) and walkable
//     Bale Loft / Skillion Roof (y = 2.6) with a steel grate catwalk projecting toward the creek
//   • left lane (+X for Alpha, -X for Bravo): Red Sand Hills & Flinders Sandstone Ridge — multi-tiered wind-rippled
//     red sand hills (y = 1.1 → 2.2 → 3.1) with smooth walkable slopes and sandstone rock tors
// ------------------------------------------------------------------------------------------------------------
const OB = {
  earth: '#d87a4a',         // sun-baked red-ochre station paddock claypan
  earthLt: '#e08959',       // dusty stockyard earth
  creek: '#c6683c',         // sunken dry creek bed red river-sand & cracked clay
  sand: '#de7f4e',          // wind-rippled red sand hill lower terrace
  sandHi: '#e58b58',        // sunlit red sand dune crest
  ochreRock: '#c9653b',     // layered Flinders sandstone bluff
  cliff: '#b5532d',         // outer perimeter sandstone gorge wall (off-limits)
  veranda: '#c99364',       // jarrah homestead veranda decking
  wood: '#bf8a5c',          // rough-sawn station timber dock & ramps
  shedDeck: '#c58f61',      // shearing shed bale-loft timber deck
  weatherboard: '#f2e6cf',  // sun-bleached cream homestead weatherboard
  corrugated: '#d8cfb8',    // zincalume / weathered cream corrugated iron
  concrete: '#d8cbb8',      // bore-pump concrete pad & bowser plinth
  step: '#d6baa0',          // sandstone / concrete steps
  steel: '#8c867e',         // galvanised cattle-grid & catwalk grating
};

const NOPAINT = [[1, 0, 0], [-1, 0, 0], [0, 0, 1], [0, 0, -1], [0, 1, 0]];

const redearth = (o = {}) => ({ color: OB.earth, pattern: SURF.redearth, ...o });
const creekbed = (o = {}) => ({ color: OB.creek, pattern: SURF.redearth, ...o });
const sanddune = (c = OB.sand, o = {}) => ({ color: c, pattern: SURF.sanddune, ...o });
const timber = (c = OB.wood, o = {}) => ({ color: c, pattern: SURF.outbackwood, ...o });
const cliff = (c = OB.cliff, o = {}) => ({ color: c, pattern: SURF.sanddune, noPaint: NOPAINT, roof: true, ...o });

export const LAYOUT = {
  id: 'outback',
  biome: 'outback',
  envBoats: false,
  bounds: { minX: -28, maxX: 28, minZ: -46, maxZ: 46 },
  spawnPads: [[0, 2.6, -41.8], [0, 2.6, 41.8]],
  spawnBarrier: 4.2,
  // match intro: opens on the central Southern Cross Bore Windmill over the dry creek bed, then sweeps back to the Homestead Veranda
  intro: { from: [12, 11.5, 10], lookFrom: [0, 4.5, -2], toBack: 3.4 },
  // stage-select picture: 3/4 aerial from off the NE sand hills looking SW across the bore windmill, creek bed, and Flinders Ranges
  art: { from: [30, 14.5, -34], look: [-3.5, 2.8, 4.5], fov: 60 },

  // ==========================================================================================================
  // SINGLE — Central Dry Creek Bed, Southern Cross Bore Windmill Octagon, and Cattle-Grid Bridges
  // ==========================================================================================================
  single: [
    // Sunken dry creek bed floor (y = -0.7, z = -8.2..8.2) across the full width of the gorge
    B(-26.0, 26.0, -1.4, -0.7, -8.2, 8.2, creekbed({ tag: 'creek-bed' })),

    // Central Bore Windmill stepped octagonal staging & compass-stencil concrete pad
    ...OCT(0, 0, 3.9, -0.7, 0.22, sanddune(OB.ochreRock)),
    B(-2.7, 2.7, -0.7, 0.42, -2.7, 2.7, {
      color: OB.concrete,
      pattern: PATTERN.concrete,
      tag: 'bore-platform',
      mural: [{ n: [0, 1, 0], id: MURAL_IDS.borePlatform }],
    }),
    // Stone steps up from the dry creek bed onto the central bore platform (±X sides)
    R([-5.2, -0.7, 0], [-2.7, 0.42, 0], 2.6, { color: OB.step, pattern: PATTERN.stonestep }),
    R([5.2, -0.7, 0], [2.7, 0.42, 0], 2.6, { color: OB.step, pattern: PATTERN.stonestep }),

    // Twin Cattle-Grid Bridges across the dry creek bed (x = ±10.2, y = 0.08)
    // West bridge (-X): timber approaches + central steel grate cattle grid (kids cross, squids drop through)
    B(-12.1, -8.3, -0.2, 0.08, -8.4, -3.2, timber(OB.wood, { tag: 'bridge-west-n' })),
    B(-12.1, -8.3, -0.06, 0.08, -3.2, 3.2, { color: OB.steel, pattern: PATTERN.grate, grate: true, tag: 'cattle-grid-west' }),
    B(-12.1, -8.3, -0.2, 0.08, 3.2, 8.4, timber(OB.wood, { tag: 'bridge-west-s' })),
    // West bridge timber sleeper piers in the creek bed
    B(-12.0, -11.5, -0.7, 0.08, -3.4, -2.9, timber(OB.veranda)),
    B(-8.9, -8.4, -0.7, 0.08, -3.4, -2.9, timber(OB.veranda)),
    B(-12.0, -11.5, -0.7, 0.08, 2.9, 3.4, timber(OB.veranda)),
    B(-8.9, -8.4, -0.7, 0.08, 2.9, 3.4, timber(OB.veranda)),

    // East bridge (+X): timber approaches + central steel grate cattle grid
    B(8.3, 12.1, -0.2, 0.08, -8.4, -3.2, timber(OB.wood, { tag: 'bridge-east-n' })),
    B(8.3, 12.1, -0.06, 0.08, -3.2, 3.2, { color: OB.steel, pattern: PATTERN.grate, grate: true, tag: 'cattle-grid-east' }),
    B(8.3, 12.1, -0.2, 0.08, 3.2, 8.4, timber(OB.wood, { tag: 'bridge-east-s' })),
    // East bridge timber sleeper piers in the creek bed
    B(8.4, 8.9, -0.7, 0.08, -3.4, -2.9, timber(OB.veranda)),
    B(11.5, 12.0, -0.7, 0.08, -3.4, -2.9, timber(OB.veranda)),
    B(8.4, 8.9, -0.7, 0.08, 2.9, 3.4, timber(OB.veranda)),
    B(11.5, 12.0, -0.7, 0.08, 2.9, 3.4, timber(OB.veranda)),

    // Creek-bed weathered sandstone boulders / stepping blocks (cover inside the -0.7 m trench)
    O(-6.0, -4.2, 2.4, 2.0, -0.7, 0.35, 18, sanddune(OB.ochreRock)),
    O(6.0, 4.2, 2.4, 2.0, -0.7, 0.35, 18, sanddune(OB.ochreRock)),
    O(-14.6, 2.6, 2.6, 1.8, -0.7, 0.45, -14, sanddune(OB.ochreRock)),
    O(14.6, -2.6, 2.6, 1.8, -0.7, 0.45, -14, sanddune(OB.ochreRock)),
    O(-21.2, -1.8, 3.0, 2.2, -0.7, 0.55, 12, sanddune(OB.ochreRock)),
    O(21.2, 1.8, 3.0, 2.2, -0.7, 0.55, 12, sanddune(OB.ochreRock)),

    // Outer gorge sandstone cliff walls sealing the east/west ends of the dry creek bed
    B(-28.0, -26.0, -1.4, 4.8, -8.6, 8.6, cliff(OB.cliff)),
    B(26.0, 28.0, -1.4, 4.8, -8.6, 8.6, cliff(OB.cliff)),
  ],

  // ==========================================================================================================
  // HALF — Alpha side (-Z), mirrored 180° (x → -x, z → -z) for Bravo (+Z)
  // ==========================================================================================================
  half: [
    // ---------------------------------------------------------------- 1. Continuous Station Paddock Base (y = 0)
    B(-26.0, 26.0, -1.2, 0.0, -45.4, -8.2, redearth({ tag: 'station-yard' })),

    // ---------------------------------------------------------------- 2. Homestead Veranda Spawn (y = 2.6)
    // Main veranda timber deck (18 x 7.2 m top face with mural 6)
    B(-9.0, 9.0, 0.0, 2.6, -45.4, -38.2, timber(OB.veranda, {
      tag: 'spawn-veranda',
      mural: [{ n: [0, 1, 0], id: MURAL_IDS.verandaDeck }],
    })),
    // Flanking sandstone veranda wings (x = -14..-9 and 9..14)
    B(-14.0, -9.0, 0.0, 2.6, -45.4, -39.5, sanddune(OB.sandHi)),
    B(9.0, 14.0, 0.0, 2.6, -45.4, -39.5, sanddune(OB.sandHi)),
    // Homestead weatherboard facade backwall along z = -46..-45.4 (scenery / off-limits roof)
    B(-14.5, 14.5, 0.0, 5.2, -46.0, -45.4, {
      color: OB.weatherboard,
      pattern: PATTERN.weatherboard,
      noPaint: NOPAINT,
      roof: true,
    }),
    // Outer spawn gorge cliff corners (x = ±14.0..±28 flush with spawn wings along the back boundary)
    B(-28.0, -14.0, -1.2, 5.2, -46.0, -43.2, cliff(OB.cliff)),
    B(14.0, 28.0, -1.2, 5.2, -46.0, -43.2, cliff(OB.cliff)),
    B(-28.0, -26.0, -1.2, 5.2, -43.2, -8.6, cliff(OB.cliff)),
    B(26.0, 28.0, -1.2, 5.4, -43.2, -8.6, cliff(OB.cliff)),

    // Veranda front low parapet walls (shielding spawn from mid sightlines while leaving stairs open)
    B(-8.8, -3.2, 2.6, 3.45, -38.7, -38.2, { color: OB.weatherboard, pattern: PATTERN.weatherboard, noNav: true }),
    B(3.2, 8.8, 2.6, 3.45, -38.7, -38.2, { color: OB.weatherboard, pattern: PATTERN.weatherboard, noNav: true }),

    // Three exit routes from Homestead Veranda:
    // (a) Central wide stone stair down to the Station Yard (y = 0..2.6)
    R([0, 0.0, -32.2], [0, 2.6, -38.2], 5.2, { color: OB.step, pattern: PATTERN.stonestep }),
    // (b) West timber ramp toward the Shearing Shed & Stockyards (-X lane)
    R([-14.5, 0.0, -34.2], [-9.2, 2.6, -41.5], 3.0, { color: OB.wood, pattern: PATTERN.rampboard }),
    // (c) East timber ramp onto the Lower Red Sand Hill (+X lane, y = 1.1..2.6)
    R([15.2, 1.1, -33.2], [9.2, 2.6, -41.5], 3.0, { color: OB.wood, pattern: PATTERN.rampboard }),

    // ---------------------------------------------------------------- 3. Station Yard & Creek Bank (y = 0 -> -0.7)
    // Station Diesel Bowser & Generator Shed (mural 8 on its +Z wall facing mid)
    B(-4.6, 1.2, 0.0, 0.32, -26.0, -21.6, { color: OB.concrete, pattern: PATTERN.concrete }),
    B(-4.2, -0.6, 0.32, 2.52, -25.4, -22.2, {
      color: OB.corrugated,
      pattern: PATTERN.corrugated,
      roof: true,
      mural: [{ n: [0, 0, 1], id: MURAL_IDS.roadSign }],
    }),
    // Yard cover crate / sandstone outcrop block on the east side of the yard
    B(4.2, 7.6, 0.0, 1.15, -29.0, -26.6, sanddune(OB.ochreRock)),
    // Mid-yard stockyard loading block near the west bridge approach
    B(-7.4, -4.4, 0.0, 1.05, -15.4, -13.0, timber(OB.wood)),

    // Earth & sand slipways sloping down from the Station Yard (y = 0) into the Dry Creek Bed (y = -0.7)
    R([0.0, -0.7, -4.5], [0.0, 0.0, -8.2], 4.8, creekbed()),
    R([-16.2, -0.7, -4.6], [-16.2, 0.0, -8.2], 4.2, creekbed()),
    R([16.2, -0.7, -4.6], [16.2, 0.0, -8.2], 4.2, sanddune(OB.sand)),

    // Creek-bank sandstone retaining ledges flanking the central slipway (crouch cover overlooking mid)
    B(-6.8, -3.2, 0.0, 0.85, -9.2, -8.2, sanddune(OB.ochreRock)),
    B(3.2, 6.8, 0.0, 0.85, -9.2, -8.2, sanddune(OB.ochreRock)),

    // ---------------------------------------------------------------- 4. Right Lane (-X): Shearing Shed & Stockyards
    // Timber Loading Dock (y = 1.2)
    B(-26.0, -12.8, 0.0, 1.2, -29.5, -13.5, timber(OB.wood, { tag: 'woolshed-dock' })),
    // Ramps up from the Station Yard onto the Loading Dock (y = 0..1.2)
    R([-9.6, 0.0, -21.5], [-12.8, 1.2, -21.5], 3.4, { color: OB.wood, pattern: PATTERN.rampboard }),
    R([-19.5, 0.0, -9.8], [-19.5, 1.2, -13.5], 3.4, { color: OB.wood, pattern: PATTERN.rampboard }),

    // Walkable Shearing Shed Bale Loft (y = 2.6, 10.5 x 11 m top face with mural 4, 11 x 1.4 m +X wall with mural 5)
    B(-26.0, -15.5, 1.2, 2.6, -26.0, -15.0, timber(OB.shedDeck, {
      tag: 'woolshed-loft',
      mural: [
        { n: [0, 1, 0], id: MURAL_IDS.shedRoof },
        { n: [1, 0, 0], id: MURAL_IDS.shedWall },
      ],
    })),
    // Back timber ramp from the loading dock up onto the Bale Loft (y = 1.2..2.6)
    R([-20.8, 1.2, -29.3], [-20.8, 2.6, -26.0], 3.2, { color: OB.wood, pattern: PATTERN.rampboard }),
    // Stepped bale/timber platform on the east edge of the dock for quick hops onto the loft (y = 1.2..1.9)
    B(-15.5, -13.2, 1.2, 1.9, -18.6, -15.2, timber(OB.veranda)),
    // Zone Control attacker ramp on the creek side of the Shearing Shed loft (y = 1.2..2.6)
    R([-23.2, 1.2, -11.4], [-23.2, 2.6, -15.0], 2.8, {
      color: OB.wood,
      pattern: PATTERN.rampboard,
      onlyIn: 'zones',
    }),
    B(-25.2, -21.2, 0.0, 1.2, -13.5, -11.0, timber(OB.wood, { onlyIn: 'zones' })),

    // Steel Grate Catwalk projecting from the Shearing Shed loft toward the Dry Creek Bed (y = 2.6)
    B(-18.6, -15.5, 2.38, 2.6, -15.0, -9.2, {
      color: OB.steel,
      pattern: PATTERN.grate,
      grate: true,
      tag: 'woolshed-catwalk',
    }),

    // ---------------------------------------------------------------- 5. Left Lane (+X): Red Sand Hills & Sandstone Ridge
    // Lower Red Sand Hill Terrace (y = 1.1)
    B(12.8, 26.0, 0.0, 1.1, -35.5, -9.2, sanddune(OB.sand, { tag: 'sand-hill-low' })),
    // Smooth walkable sand slopes up from the Station Yard and Creek Bank onto the Lower Sand Hill (y = 0..1.1)
    R([9.2, 0.0, -24.5], [12.8, 1.1, -24.5], 5.8, sanddune(OB.sand)),
    R([15.4, 0.0, -5.6], [15.4, 1.1, -9.2], 4.6, sanddune(OB.sand)),
    R([18.5, 0.0, -38.8], [18.5, 1.1, -35.5], 5.0, sanddune(OB.sand)),

    // Main Red Sand Ridge (y = 2.2)
    B(16.2, 26.0, 1.1, 2.2, -28.8, -11.2, sanddune(OB.sandHi, { tag: 'sand-hill-mid' })),
    // Walkable sand dune slopes up onto the Main Red Sand Ridge (y = 1.1..2.2)
    R([19.8, 1.1, -32.6], [19.8, 2.2, -28.8], 5.0, sanddune(OB.sandHi)),
    R([12.9, 1.1, -15.2], [16.2, 2.2, -15.2], 4.4, sanddune(OB.sandHi)),
    R([20.2, 1.1, -8.0], [20.2, 2.2, -11.2], 4.2, sanddune(OB.sandHi)),

    // Upper Flinders Sandstone Bluff Lookout (y = 3.1)
    B(19.6, 26.0, 2.2, 3.1, -21.6, -13.0, sanddune(OB.ochreRock, { tag: 'sandstone-lookout' })),
    R([16.6, 2.2, -21.2], [19.6, 3.1, -21.2], 3.6, sanddune(OB.ochreRock)),
    // Weathered sandstone outcrop tor on the lookout lip (cover against mid & opposite shed)
    B(19.6, 21.0, 3.1, 4.05, -15.6, -13.2, sanddune(OB.ochreRock, { noNav: true })),
    // Secondary sandstone boulder on the lower sand shelf
    O(14.6, -29.8, 2.4, 1.8, 1.1, 2.1, -22, sanddune(OB.ochreRock)),
  ],

  decor: {
    lamps: [],
    lampPools: [[-11.8, -33.0], [11.8, -33.0], [-12.2, -12.8], [12.2, -12.8]],
    palms: [],
    flags: [[-9.2, 2.6, -44.8], [9.2, 2.6, -44.8]],
  },
};
